import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './DeclarantForm.css';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence } from 'framer-motion';
import { AxiosError } from 'axios';

interface DeclarantData {
  type_of_person: string;
  name: string;
  cpf: string;
  date_of_birth: string;                 // DD/MM/AAAA (UI)
  sex: string;
  start_date_of_representation: string;  // DD/MM/AAAA (UI)
  end_date_of_representation: string;    // DD/MM/AAAA (UI)
  telephone: string;
  identity_document: string;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const labelMap: Record<keyof DeclarantData, string> = {
  type_of_person: 'Tipo de Pessoa',
  name: 'Nome',
  cpf: 'CPF',
  date_of_birth: 'Data de Nascimento',
  sex: 'Sexo',
  start_date_of_representation: 'Início da Representação',
  end_date_of_representation: 'Fim da Representação',
  telephone: 'Telefone',
  identity_document: 'Documento de Identidade',
};

// Converte "DD/MM/AAAA" -> "AAAA-MM-DD" ou null se vazio/ inválido
const toISOorNull = (brDate: string): string | null => {
  if (!brDate) return null;
  const [d, m, y] = brDate.split('/');
  if (!d || !m || !y) return null;
  return `${y}-${m}-${d}`;
};

const DeclarantForm: React.FC = () => {
  const { token, id_user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<DeclarantData>({
    type_of_person: '',
    name: '',
    cpf: '',
    date_of_birth: '',
    sex: '',
    start_date_of_representation: '',
    end_date_of_representation: '',
    telephone: '',
    identity_document: '',
  });

  const [hasExisting, setHasExisting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isFormValid = () => {
    const required: (keyof DeclarantData)[] = [
      'type_of_person',
      'name',
      'cpf',
      'date_of_birth',
      'sex',
      'telephone',
      'identity_document',
    ];
    if (formData.type_of_person === 'Representante legal') {
      required.push('start_date_of_representation'); // fim é opcional
    }
    return required.every((f) => typeof formData[f] === 'string' && formData[f].trim() !== '');
  };

  const isValidDate = (dateStr: string): boolean => {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateStr)) return false;
    const [day, month, year] = dateStr.split('/').map(Number);
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > new Date().getFullYear()) return false;
    const d = new Date(year, month - 1, day);
    return d.getDate() === day && d.getMonth() === month - 1 && d.getFullYear() === year;
  };

  useEffect(() => {
    if (!token || !id_user) {
      alert('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    const fetchDeclarant = async () => {
      try {
        const response = await api.get(`/declarant/${id_user}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.message === 'Declarant retrieved' && response.data.data) {
          const data = response.data.data;
          setFormData({
            type_of_person: data.type_of_person || '',
            name: data.name || '',
            cpf: data.cpf ? formatCPF(String(data.cpf)) : '',
            date_of_birth: data.date_of_birth
              ? new Date(data.date_of_birth).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '',
            sex: data.sex || '',
            start_date_of_representation: data.start_date_of_representation
              ? new Date(data.start_date_of_representation).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '',
            end_date_of_representation: data.end_date_of_representation
              ? new Date(data.end_date_of_representation).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '',
            telephone: data.telephone ? formatPhone(String(data.telephone)) : '',
            identity_document: data.identity_document || '',
          });
          setHasExisting(true);
          setErrorMessage(null);
        } else {
          setHasExisting(false);
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao buscar declarante:', error.response?.status, error.response?.data);
        setHasExisting(false);
        // Não bloqueia a tela — usuário pode criar
        setErrorMessage(error.response?.data?.message || null);
      }
    };

    fetchDeclarant();
  }, [token, id_user, navigate]);

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})$/, '$1.$2.$3-$4');
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    return cleaned
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setErrorMessage(null);

    if (name === 'type_of_person') {
      // Se não for Representante legal, limpa datas de representação
      setFormData((prev) => ({
        ...prev,
        type_of_person: value,
        start_date_of_representation: value === 'Representante legal' ? prev.start_date_of_representation : '',
        end_date_of_representation: value === 'Representante legal' ? prev.end_date_of_representation : '',
      }));
      return;
    }

    if (name === 'cpf') {
      setFormData((prev) => ({ ...prev, cpf: formatCPF(value) }));
      return;
    }

    if (name === 'telephone') {
      setFormData((prev) => ({ ...prev, telephone: formatPhone(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2);
    if (v.length > 5) v = v.slice(0, 5) + '/' + v.slice(5, 9);
    setFormData((prev) => ({ ...prev, [e.target.name]: v }));
    setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    if (!isFormValid()) {
      setErrorMessage('Por favor, preencha todos os campos obrigatórios.');
      setIsLoading(false);
      return;
    }

    if (!isValidDate(formData.date_of_birth)) {
      setErrorMessage('Data de nascimento inválida. Use o formato DD/MM/AAAA e insira uma data válida.');
      setIsLoading(false);
      return;
    }

    if (formData.type_of_person === 'Representante legal') {
      if (!isValidDate(formData.start_date_of_representation)) {
        setErrorMessage('Data de início da representação inválida. Use o formato DD/MM/AAAA e insira uma data válida.');
        setIsLoading(false);
        return;
      }
      if (formData.end_date_of_representation && !isValidDate(formData.end_date_of_representation)) {
        setErrorMessage('Data de fim da representação inválida. Use o formato DD/MM/AAAA e insira uma data válida.');
        setIsLoading(false);
        return;
      }
    }

    try {
      const cpfDigits = formData.cpf.replace(/\D/g, '');
      const telDigits = formData.telephone.replace(/\D/g, '');

      // payload base
      const dataToSend: Record<string, any> = {
        type_of_person: formData.type_of_person,
        name: formData.name,
        cpf: cpfDigits,
        date_of_birth: toISOorNull(formData.date_of_birth), // sempre obrigatório
        sex: formData.sex,
        telephone: telDigits,
        identity_document: formData.identity_document,
      };

      // somente se for criação, envie id_user
      if (!hasExisting) {
        dataToSend.id_user = id_user;
      }

      // Só envia datas de representação se for Representante legal
      if (formData.type_of_person === 'Representante legal') {
        const startISO = toISOorNull(formData.start_date_of_representation); // obrigatório neste caso
        const endISO = toISOorNull(formData.end_date_of_representation);     // opcional

        if (startISO) dataToSend.start_date_of_representation = startISO;
        if (endISO) dataToSend.end_date_of_representation = endISO;
      }

      // remove chaves vazias/null/undefined
      Object.keys(dataToSend).forEach((k) => {
        if (
          dataToSend[k] === '' ||
          dataToSend[k] === null ||
          dataToSend[k] === undefined
        ) {
          delete dataToSend[k];
        }
      });

      const url = hasExisting ? `/declarant/${id_user}` : '/declarant';
      const method = hasExisting ? 'put' : 'post';

      await api({
        method,
        url,
        data: dataToSend,
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/rj/documents');
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao salvar declarante:', error.response?.status, error.response?.data);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erro ao salvar o declarante. Tente novamente.';
      setErrorMessage(message);
      setIsLoading(false);
    }
  };

  const isRepLegal = formData.type_of_person === 'Representante legal';

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="declarant-wrapper">
          <WizardStepper current={4} />
          <div className="declarant-form-container">
            <h2>Formulário do Declarante</h2>
            <AnimatePresence>
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
            </AnimatePresence>

        <form className="declarant-form" onSubmit={handleSubmit}>
          {(Object.entries(formData) as [keyof DeclarantData, string][]).map(([key, value]) => {
            // some de datas de representação se não for representante legal
            if ((key === 'start_date_of_representation' || key === 'end_date_of_representation') && !isRepLegal) {
              return null;
            }

            let inputElement: React.ReactNode;


            if (key === 'type_of_person') {
              inputElement = (
                <select
                  id={key}
                  name={key}
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="Servidor">Servidor</option>
                  <option value="Tutor">Tutor</option>
                  <option value="Curador">Curador</option>
                  <option value="Representante legal">Representante legal</option>
                </select>
              );
            } else if (key === 'sex') {
              inputElement = (
                <select
                  id={key}
                  name={key}
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              );
            } else if (
              key === 'date_of_birth' ||
              key === 'start_date_of_representation' ||
              key === 'end_date_of_representation'
            ) {
              const isStart = key === 'start_date_of_representation';
              const isEnd = key === 'end_date_of_representation';

              const requiredFlag =
                key === 'date_of_birth' ? true :
                isStart ? isRepLegal :
                false; // end_date nunca obrigatória

              inputElement = (
                <input
                  id={key}
                  type="text"
                  name={key}
                  value={value}
                  onChange={handleDateChange}
                  maxLength={10}
                  placeholder="DD/MM/AAAA"
                  className="form-input"
                  required={requiredFlag}
                  disabled={isLoading || (!isRepLegal && (isStart || isEnd))}
                />
              );
            } else {
              inputElement = (
                <input
                  id={key}
                  name={key}
                  value={value}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={
                    key === 'cpf' ? '000.000.000-00' :
                    key === 'telephone' ? '(00) 00000-0000' :
                    ''
                  }
                  maxLength={key === 'cpf' ? 14 : key === 'telephone' ? 15 : undefined}
                  required
                  disabled={isLoading}
                />
              );
            }

            return (
              <div className="form-group" key={key}>
                <label htmlFor={key}>{labelMap[key]}:</label>
                {inputElement}
              </div>
            );
          })}

          <div className="button-group">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/rj/dependents')}
              disabled={isLoading}
              aria-label="Voltar para o formulário de dependentes"
            >
              Anterior
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={!isFormValid() || isLoading}
              aria-label="Salvar declarante e prosseguir"
            >
              {isLoading ? 'Salvando…' : 'Próximo'}
            </Button>
          </div>
        </form>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default DeclarantForm;
